import type { Nullable } from '@src/shared/types/common';

import type { DataElement, GalleryItem, IGallery, Options } from './types';
import { FileType } from './types';

export class Gallery implements IGallery {
    private readonly _imageExtensions = new Set(['jpg', 'jpeg', 'webp', 'png']);
    private readonly _videoExtensions = new Set(['avi', 'mp4']);
    private readonly _validTypes = new Set([FileType.img, FileType.video]);
    private readonly _baseUrl;
    private readonly _wrapper;
    private readonly _mainContainer;
    private readonly _subContainer;
    private _subElementSelected: HTMLElement;
    private _fancyboxIndex = 0;
    private _galleryItems: GalleryItem[] = [];

    constructor(id: string, { baseUrl }: Options) {
        this._wrapper = $(`#${id}`);
        this._baseUrl = baseUrl;

        this._mainContainer = this._wrapper.find('.photo-container');
        this._subContainer = this._wrapper.find('.photo-gallery');

        this.attachClickHandler();
    }

    /**
     * строка такого типа: "jpg; jpeg; png; webp"
     * */
    private getTypeFile(extensions: string) {
        const extensionList = extensions.split(/;\s+/);

        for (const ext of extensionList) {
            if (this._imageExtensions.has(ext)) {
                return FileType.img;
            }
            else if (this._videoExtensions.has(ext)) {
                return FileType.video;
            }
        }

        return null;
    }

    private getValidElements(imgArr: DataElement[]): DataElement[] {
        return imgArr
            .filter((item) => {
                const fileType = this.getTypeFile(item.FileType.FileExtensionsToString);
                return fileType === null ? false : this._validTypes.has(fileType);
            })
            .sort((a, b) => a.PhotoNumber - b.PhotoNumber);
    }

    private getFileUrl(fileId: number | string = '') {
        return `${this._baseUrl}?&fileId=${fileId}`;
    }

    private createImgTemplate(className: string, src: string) {
        return `<img class="${className}" loading="lazy" src="${src}&stream=true"/>`;
    }

    private createVideoTemplate(className: string, src: string) {
        return `
            <a class="${className} bg-circle-video"
               src="${src}&stream=true"
               data-type="html5video">
            </a>
        `;
    }

    private createTemplate(fileType: Nullable<FileType>, className: string, src: string) {
        return this[fileType === FileType.img ? 'createImgTemplate' : 'createVideoTemplate'](className, src);
    }

    private createSubTemplate(extensions: string, id: number, index: number) {
        const src = this.getFileUrl(id);

        return `
            <div class="photo-gallery__item" data-extensions="${extensions}" data-id="${id}" data-index="${index}">
                ${this.createTemplate(this.getTypeFile(extensions), 'photo-gallery__item-img', src)}
                <a class="photo-gallery__download" href="${src}" download></a>
            </div>
        `;
    }

    private setMainElement({ FileType: { FileExtensionsToString }, Id }: DataElement) {
        this._mainContainer.html(
            this.createTemplate(this.getTypeFile(FileExtensionsToString), 'photo-container__img', this.getFileUrl(Id))
        );
    }

    private setSubElements(dataElements: DataElement[]) {
        const firstElement = dataElements[0];

        this._subContainer.html(`
            ${this.createSubTemplate(firstElement.FileType.FileExtensionsToString, firstElement.Id, 0)}
            ${dataElements.slice(1).reduce((result, { FileType: { FileExtensionsToString }, Id }, index) =>
            result + this.createSubTemplate(FileExtensionsToString, Id, index + 1), '')}
        `);
    }

    private attachClickHandler() {
        this._mainContainer.on('click', (event) => {
            const container = event.currentTarget;
            event.preventDefault();

            if (container.children.length) {
                window.Fancybox.show(this._galleryItems, {
                    Carousel: {
                        Autoplay: {
                            timeout: 1500
                        }
                    },
                    startIndex: this._fancyboxIndex
                });
            }
        });
        this._subContainer.on('click', ({ target }) => {
            const classList = target.classList;

            if (classList.contains('photo-gallery__item')) {
                this._subElementSelected.classList.remove('photo-gallery__item_active');
                classList.add('photo-gallery__item_active');

                this._subElementSelected = target;

                const { extensions = '', index, id = '' } = target.dataset;
                this._fancyboxIndex = Number(index);
                this.setMainElement({ FileType: { FileExtensionsToString: extensions }, Id: Number(id), PhotoNumber: 0 });
            }
        });
    }

    public setData(data: DataElement[]) {
        this._fancyboxIndex = 0;
        const validImages = this.getValidElements(data);
        this._galleryItems = validImages.map((item) => {
            const fileType = this.getTypeFile(item.FileType.FileExtensionsToString);

            return fileType === FileType.img ? {
                src: `${this.getFileUrl(item.Id)}&stream=true`,
                type: 'image'
            } : {
                src: `${this.getFileUrl(item.Id)}&autoplay=0&stream=true`,
                type: 'iframe',
                width: 1000,
                height: 600
            };
        });

        if (!validImages.length) {
            this._mainContainer.html('');
            this._subContainer.html('');
            return;
        }

        this.setMainElement(validImages[0]);
        this.setSubElements(validImages);

        const firstSubElement = this._subContainer.children().first();
        firstSubElement.addClass('photo-gallery__item_active');
        this._subElementSelected = firstSubElement[0];
    }
}
